import { FC } from 'react';
import { Alert } from 'Components/nui';
import * as Data from 'Components/Data';
import { FormattedMessage } from 'react-intl';
import { CustomerPrice, PortPrice, Price } from './types';
import { PriceReasonIcon } from './PriceReasonIcon';

interface PriceListProps {
  prices: CustomerPrice[] | PortPrice[];
  format: 'customer' | 'port';
  title?: React.ReactNode;
  zones?: boolean;
}

// Lifted FormattedMessage for customer count - must be static for extraction tools
const CustomerCountMessage: FC<{ count: number }> = ({ count }) => (
  <FormattedMessage
    id="price-list-customer-count"
    defaultMessage="{count} {count, plural, one {customer} other {customers}}"
    values={{ count }}
  />
);

// Shared component for rendering price with unit or warning icon
const PriceDisplay: FC<{ price: Price }> = ({ price }) => {
  return (
    <span className="tw tw:flex tw:items-center tw:gap-1">
      {price.val > 0 ? (
        <>
          <span className="tw tw:font-medium">
            <Data.Number value={price.val} />
          </span>
          <span className="tw tw:text-gray-500 tw:text-xs">{price.unit}</span>
        </>
      ) : (
        <PriceReasonIcon reason={price.reason || 'N/A'} />
      )}
    </span>
  );
};

const CustomerPriceList: FC<{ prices: CustomerPrice[] }> = ({ prices }) => {
  const sortedPrices = [...prices].sort((a, b) => a.name.localeCompare(b.name));

  return (
    <ul className="tw tw:list-none tw:p-0 tw:m-0 tw:divide-y tw:divide-gray-200">
      {sortedPrices.map((item) => (
        <li
          key={item.id}
          className="tw tw:flex tw:justify-between tw:items-center tw:py-2 tw:gap-4"
        >
          <span className="tw tw:flex-1 tw:text-gray-900">{item.name}</span>
          <PriceDisplay price={item.price} />
        </li>
      ))}
    </ul>
  );
};

const PortPriceList: FC<{ prices: PortPrice[]; zones: boolean }> = ({
  prices,
  zones,
}) => {
  const sortedPrices = [...prices].sort((a, b) => {
    const countryCompare = a.country.name.localeCompare(b.country.name);
    if (countryCompare !== 0) return countryCompare;
    return a.port.name.localeCompare(b.port.name);
  });

  return (
    <ul className="tw tw:list-none tw:p-0 tw:m-0 tw:divide-y tw:divide-gray-200">
      {sortedPrices.map((item) => (
        <li
          key={item.id}
          className="tw tw:flex tw:justify-between tw:items-center tw:py-2 tw:gap-4 tw:flex-wrap"
        >
          <span className="tw tw:flex-1 tw:text-gray-900 tw:min-w-[200px]">
            {item.country.name} - {item.port.name}
            {zones && item.zone?.name && (
              <span className="tw tw:text-gray-500"> ({item.zone.name})</span>
            )}
          </span>
          <PriceDisplay price={item.price} />
          <span className="tw tw:text-gray-500 tw:text-xs tw:min-w-[100px] tw:text-right">
            <CustomerCountMessage count={item.count} />
          </span>
        </li>
      ))}
    </ul>
  );
};

export const PriceList: FC<PriceListProps> = ({
  prices,
  format,
  title,
  zones = false,
}) => {
  if (prices.length === 0) {
    return (
      <Alert hasicon>
        <FormattedMessage
          id="no-data-to-display"
          defaultMessage="No data to display"
        />
      </Alert>
    );
  }

  return (
    <>
      {title && (
        <h2 className="tw tw:text-lg tw:font-light tw:text-gray-700">
          {title}
        </h2>
      )}
      <div className="tw tw:border-t tw:border-b tw:border-border">
        <div className="tw tw:max-h-[400px] tw:overflow-y-auto">
          {format === 'customer' && (
            <CustomerPriceList prices={prices as CustomerPrice[]} />
          )}
          {format === 'port' && (
            <PortPriceList prices={prices as PortPrice[]} zones={zones} />
          )}
        </div>
      </div>
    </>
  );
};
